// The alignment transform between two slides, and the view math that applies it.
//
// COORDINATE SPACE. Everything here works in BASE-LEVEL DISPLAY-ORIENTED PIXELS:
// origin at the top-left of the slide as the viewer shows it, x right, y down,
// isotropic. Two alternatives were rejected:
//
//   - Normalized [0,1] fractions SHEAR under rotation whenever the slide isn't
//     square (x/W and y/H scale differently). Alignment is inherently rotational,
//     so fractions are disqualified. This is exactly why the old delta-based
//     compare sync misbehaved: summing Δcx/Δcy fractions is only correct at
//     identical aspect AND identical zoom.
//   - Physical mm is principled but undefined when pixel spacing is synthetic
//     (pixelSpacingMm() returns null), which would give the model two code paths.
//     Physical scale is instead used as a PRIOR on `scale` by the estimator.
//
// Base display px always exist and are isotropic, so rotation is conformal.
//
// Note what `scale` means here: B base px per A base px. Two slides scanned at
// different objective magnifications give scale ≈ mmPerPx_A / mmPerPx_B (≈0.5 for
// a 40× A against a 20× B) even when physically aligned perfectly. Only the
// RESIDUAL scale, after the estimator normalizes both rasters to a common mm/px,
// sits near 1.

/** Maps a point on slide A to the corresponding point on slide B:
 *      p_B = scale · Rot(rotationDeg) · p_A + (tx, ty)
 *  `rotationDeg` is clockwise (the sense of CSS rotate() and of
 *  ViewportEngine's rotationDeg, since y points down). tx/ty are B base px. */
export interface SimilarityTransform {
  scale: number;
  rotationDeg: number;
  tx: number;
  ty: number;
}

export const IDENTITY: SimilarityTransform = { scale: 1, rotationDeg: 0, tx: 0, ty: 0 };

export interface Point {
  x: number;
  y: number;
}

/** A view's placement over its slide, in base display px. The engine speaks this. */
export interface ViewGeometry {
  /** View centre, base display px from the slide's top-left. */
  cx: number;
  cy: number;
  /** Base display px per CSS px (i.e. the OpenLayers resolution). */
  resolution: number;
  /** View rotation in degrees CW, relative to the slide's display orientation. */
  rotationDeg: number;
  /** Size of the display-oriented base pixel matrix. */
  imageWidth: number;
  imageHeight: number;
}

/** Normalize to (-180, 180]. */
export function norm180(deg: number): number {
  const d = ((deg % 360) + 360) % 360;
  return d > 180 ? d - 360 : d;
}

/** cos/sin of a CW rotation in a y-down frame. */
function rot(deg: number): { c: number; s: number } {
  const r = (deg * Math.PI) / 180;
  return { c: Math.cos(r), s: Math.sin(r) };
}

/** Rotate (x, y) clockwise by `deg` about the origin, y down. */
function rotate(x: number, y: number, deg: number): Point {
  const { c, s } = rot(deg);
  return { x: c * x - s * y, y: s * x + c * y };
}

/** Map a point from slide A's space into slide B's. */
export function apply(t: SimilarityTransform, p: Point): Point {
  const r = rotate(p.x, p.y, t.rotationDeg);
  return { x: t.scale * r.x + t.tx, y: t.scale * r.y + t.ty };
}

/** The transform mapping B's space back to A's. */
export function invert(t: SimilarityTransform): SimilarityTransform {
  const scale = 1 / t.scale;
  const rotationDeg = norm180(-t.rotationDeg);
  const r = rotate(t.tx, t.ty, rotationDeg);
  return { scale, rotationDeg, tx: -scale * r.x, ty: -scale * r.y };
}

/** `outer ∘ inner`: apply `inner` first, then `outer`. */
export function compose(outer: SimilarityTransform, inner: SimilarityTransform): SimilarityTransform {
  const r = rotate(inner.tx, inner.ty, outer.rotationDeg);
  return {
    scale: outer.scale * inner.scale,
    rotationDeg: norm180(outer.rotationDeg + inner.rotationDeg),
    tx: outer.scale * r.x + outer.tx,
    ty: outer.scale * r.y + outer.ty,
  };
}

export function isIdentity(t: SimilarityTransform, epsilon = 1e-6): boolean {
  return (
    Math.abs(t.scale - 1) < epsilon &&
    Math.abs(norm180(t.rotationDeg)) < epsilon &&
    Math.abs(t.tx) < epsilon &&
    Math.abs(t.ty) < epsilon
  );
}

// ---------------------------------------------------------------------------
// View math
//
// A screen point q (CSS px from the viewport centre, y down) relates to an image
// point p by  q = (1/r)·Rot(ρ)·(p − c).  Requiring the same q to show p in A and
// T(p) in B, for viewports of equal size, gives the three relations below.
// ---------------------------------------------------------------------------

/** B's view geometry that puts T(p) under the same screen pixel as p in A. */
export function applyToGeometry(
  gA: ViewGeometry,
  t: SimilarityTransform,
  imageB: { imageWidth: number; imageHeight: number },
): ViewGeometry {
  const c = apply(t, { x: gA.cx, y: gA.cy });
  return {
    cx: c.x,
    cy: c.y,
    resolution: t.scale * gA.resolution,
    rotationDeg: norm180(gA.rotationDeg - t.rotationDeg),
    imageWidth: imageB.imageWidth,
    imageHeight: imageB.imageHeight,
  };
}

/** Recover the transform implied by two live views — "capture the current
 *  relative pose". Exact algebraic inverse of applyToGeometry, which is what
 *  makes lock and align one mechanism rather than two. */
export function fromViewPair(gA: ViewGeometry, gB: ViewGeometry): SimilarityTransform {
  const scale = gA.resolution > 0 ? gB.resolution / gA.resolution : 1;
  const rotationDeg = norm180(gA.rotationDeg - gB.rotationDeg);
  const r = rotate(gA.cx, gA.cy, rotationDeg);
  return { scale, rotationDeg, tx: gB.cx - scale * r.x, ty: gB.cy - scale * r.y };
}

/**
 * The neutral alignment between two slides: same physical size, same rotation,
 * slide centres coincident. This is the sensible starting point when the panes
 * are first overlaid and the user has arranged nothing yet — unlike capturing
 * whatever poses happen to be live, it is deterministic and doesn't depend on
 * when each engine settled its initial fit.
 *
 * `mmPerPx` is per base pixel and may be null (synthetic spacing), in which case
 * we fall back to assuming the two slides cover the same physical extent and
 * match them by pixel dimensions instead.
 */
export function physicalAlignment(
  a: { imageWidth: number; imageHeight: number; mmPerPx: number | null },
  b: { imageWidth: number; imageHeight: number; mmPerPx: number | null },
): SimilarityTransform {
  const scale =
    a.mmPerPx && b.mmPerPx ? a.mmPerPx / b.mmPerPx : a.imageWidth ? b.imageWidth / a.imageWidth : 1;
  return {
    scale,
    rotationDeg: 0,
    tx: b.imageWidth / 2 - scale * (a.imageWidth / 2),
    ty: b.imageHeight / 2 - scale * (a.imageHeight / 2),
  };
}

/** Adjust a transform's scale/rotation while keeping `pivotA` pinned to the same
 *  place in B — so a rotate or scale nudge turns about the viewport centre
 *  rather than about the slide's top-left corner. */
function withFixedPoint(
  t: SimilarityTransform,
  next: { scale: number; rotationDeg: number },
  pivotA: Point,
): SimilarityTransform {
  const target = apply(t, pivotA);
  const r = rotate(pivotA.x, pivotA.y, next.rotationDeg);
  return {
    scale: next.scale,
    rotationDeg: norm180(next.rotationDeg),
    tx: target.x - next.scale * r.x,
    ty: target.y - next.scale * r.y,
  };
}

/** Nudge B by a screen-pixel delta. Dragging right must move B's tissue right,
 *  so B's view centre moves LEFT in image space: Δt = −r_B · Rot(−ρ_B) · Δq. */
export function translateBy(
  t: SimilarityTransform,
  dScreenX: number,
  dScreenY: number,
  gB: ViewGeometry,
): SimilarityTransform {
  const d = rotate(dScreenX, dScreenY, -gB.rotationDeg);
  return { ...t, tx: t.tx - gB.resolution * d.x, ty: t.ty - gB.resolution * d.y };
}

/** Rotate B's content clockwise by `deltaDeg` about `pivotA` (an A-space point,
 *  normally A's view centre). ρ_B = ρ_A − rotationDeg, so turning B's content CW
 *  means DECREASING rotationDeg. */
export function rotateBy(t: SimilarityTransform, deltaDeg: number, pivotA: Point): SimilarityTransform {
  return withFixedPoint(t, { scale: t.scale, rotationDeg: t.rotationDeg - deltaDeg }, pivotA);
}

/** Show B `factor`× larger about `pivotA`. Larger means fewer B px per screen px,
 *  so scale is DIVIDED by the factor. */
export function scaleBy(t: SimilarityTransform, factor: number, pivotA: Point): SimilarityTransform {
  if (!(factor > 0)) return t;
  return withFixedPoint(t, { scale: t.scale / factor, rotationDeg: t.rotationDeg }, pivotA);
}
